"use client";

import { agreement, socialLinks } from "@/src/const";
import { deviceDetector, ospThemes } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { I18nUtil } from "@utils/i18n";
import { ShellLink } from "@/src/components";
import { OspIcons } from "@ospweb/sdk-ui/src/Icon/OspIcons";
import { getFooterLinkSections } from "@/src/config/footerLinks";
import Translations from "@translate/components/TokenDetails/Footer";
import { rootStore } from "@/src/store";
import { useMemo } from "react";
import { useSnapshot } from "valtio";

const I18n = I18nUtil.createScoped(Translations);

const socialIcons = [
  {
    name: "twitter",
    link: socialLinks.twitter,
    size: 16,
  },
  {
    name: "youtube",
    link: socialLinks.youtube,
  },
];

export const Footer = ({
  locale,
  className,
}: {
  locale: string;
  className?: string;
}) => {
  const footerData = useMemo(() => getFooterLinkSections(locale), [locale]);
  const { type } = useSnapshot(rootStore.app);
  const isExtension = useMemo(() => type === "extension", [type]);
  const isApp = deviceDetector.isApp();

  if (isApp) {
    return null;
  }

  return (
    <>
      {deviceDetector.isApp() && (
        <div className={cn("flex w-full h-10 py-8", className)} />
      )}
      {!deviceDetector.isApp() && (
        <div
          className={cn(
            "flex flex-col gap-8 border-t border-border_white_transparent10 w-full py-8 px-4  md:py-8 md:px-15",
            {
              hidden: isExtension,
            },
            className,
          )}
        >
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-6">
              {socialIcons.map((item) => {
                const { name, link, size = 20 } = item;
                return (
                  <ShellLink
                    aria-label="Footer"
                    data-openreplay-label={`Footer Link | ${link}`}
                    id="lnk-footer"
                    href={link}
                    key={name}
                    target="_blank"
                    className="flex items-center justify-center"
                  >
                    <OspIcons
                      key={name}
                      name={name}
                      size={size}
                      color={ospThemes.colors.content_primary}
                    />
                  </ShellLink>
                );
              })}
            </div>
          </div>
          <div className="flex gap-4 md:gap-15 flex-wrap">
            {footerData.map((footerItem) => {
              const { title, list } = footerItem;
              return (
                <div key={footerItem.type} className="md:flex-1">
                  <div className="title_l text-content_primary mb-4">
                    {title}
                  </div>
                  <div className="flex flex-col gap-2">
                    {list.map((item) => {
                      const { name, link, target = "_self" } = item;
                      return (
                        <ShellLink
                          aria-label="Footer"
                          data-openreplay-label={`Footer Link | ${link}`}
                          id="lnk-footer-2"
                          href={link}
                          key={name}
                          target={target}
                          prefetch={false}
                        >
                          <div className="body_medium_m min-w-[162px] w-[162px] md:w-auto text-content_tertiary">
                            {name}
                          </div>
                        </ShellLink>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex flex-col md:flex-row md:items-center text-content_tertiary gap-3 body_m">
            <div className="flex items-center gap-3">
              <ShellLink
                aria-label="Footer"
                data-openreplay-label={`Footer Link | ${agreement.termsLink}`}
                id="lnk-footer-3"
                href={agreement.termsLink}
                target="_blank"
                className="body_medium_m"
              >
                {I18n.t("Terms of Use")}
              </ShellLink>
              <ShellLink
                aria-label="Footer"
                data-openreplay-label={`Footer Link | ${agreement.privacyLink}`}
                id="lnk-footer-4"
                href={agreement.privacyLink}
                target="_blank"
                className="body_medium_m"
              >
                {I18n.t("Privacy Policy")}
              </ShellLink>
            </div>
            <div className="flex items-center gap-2 body_m">
              <span>{I18n.t("© 2026 Ed: Wealth")}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
